import utcToZonedTime from 'date-fns-tz/utcToZonedTime';
import addDays from 'date-fns/addDays';
import addHours from 'date-fns/addHours';
import differenceInHours from 'date-fns/differenceInHours';
import type { IntlShape } from 'react-intl-next';

import { TimeDisplay } from '../constants';

import { messages } from './messages';

const DAY_SECTION_LENGTH = 6; // 6 hours (24 / 4)

const DAY_HOURS = 24;
const WEEK_HOURS = 24 * 7;
const MAX_DST_HOURS = 2; // Maximum DST offset

/**
 * Generates timeline displays (for day every 6 hours, for week every day, for month every day, etc.)
 * @returns Array of [start, end] for each section of timeline
 */
const generateRanges = ({
	startEpoch,
	endEpoch,
	offset,
}: {
	startEpoch: number;
	endEpoch: number;
	offset: number;
}) => {
	const times = [];

	const hours = differenceInHours(endEpoch, startEpoch);

	const startDate = utcToZonedTime(startEpoch + offset, 'UTC');

	if (hours <= DAY_HOURS + MAX_DST_HOURS) {
		// Every 6 hours
		for (let i = 0; i < DAY_HOURS; i += DAY_SECTION_LENGTH) {
			const start = addHours(startDate, i);
			const end = addHours(start, DAY_SECTION_LENGTH);
			times.push([start, end]);
		}
	} else {
		// It will be always between either 6.5 - 7.5 days or 13.5 - 14.5 days ...
		const days = Math.round(hours / 24);

		// Every day
		for (let i = 0; i < days; i++) {
			const start = addDays(startDate, i);
			const end = addDays(start, 1);

			times.push([start, end]);
		}
	}

	return times;
};

/**
 * Depending on difference between start and end epoch, returns oriantation of time (time header)
 * @returns TimeDisplay of the timeline (hour, horizontal day, vertical day)
 */
const getTimeDisplay = (start: number | Date, end: number | Date) => {
	const hours = differenceInHours(end, start);

	if (hours <= DAY_HOURS + MAX_DST_HOURS) {
		return TimeDisplay.Hour;
	}

	if (hours <= WEEK_HOURS + MAX_DST_HOURS) {
		return TimeDisplay.HorizontalDay;
	}

	return TimeDisplay.VerticalDay;
};

/**
 * Generates date strings for a given display (hour, horizontal day, vertical day)
 * @returns [firstTime, secondTime]
 */
const generateDateStrings = ({
	intlApi,
	display,
	date,
}: {
	intlApi: IntlShape;
	display: TimeDisplay;
	date: Date;
}) => {
	switch (display) {
		case TimeDisplay.Hour:
			return [
				intlApi.formatMessage(messages.weekDayShortDayNumeric, {
					weekDayShort: intlApi
						.formatDate(date, {
							weekday: 'short',
						})
						.toUpperCase(),
					dayNumeric: intlApi.formatDate(date, {
						day: 'numeric',
					}),
				}),
				intlApi.formatDate(date, {
					hour: '2-digit',
					minute: '2-digit',
				}),
			];
		case TimeDisplay.HorizontalDay:
			return [
				intlApi.formatMessage(messages.weekDayShortDayNumeric, {
					weekDayShort: intlApi
						.formatDate(date, {
							weekday: 'short',
						})
						.toUpperCase(),
					dayNumeric: intlApi.formatDate(date, {
						day: 'numeric',
					}),
				}),
			];
		case TimeDisplay.VerticalDay:
			return [
				intlApi.formatDate(date, {
					weekday: 'narrow',
				}),
				intlApi.formatDate(date, {
					day: 'numeric',
				}),
			];
	}
};

export { generateRanges, getTimeDisplay, generateDateStrings };
